import React from "react";
import styled, { css } from "styled-components";
import { Card, Skeleton } from "../../../../design-system";

// Shared layout + funnel primitives for the Messaging screen. Mirrors the visual
// language of the Analytics screen so both dashboards feel like one product.

export const SectionSpacing = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
`;

const StatLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const StatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--color-accent-subtle);
  color: var(--color-accent-text);
  border-radius: var(--radius-sm);
`;

const StatValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
  margin-top: var(--space-3);
`;

export const StatHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: var(--space-2);
`;

export const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  hint?: string;
  loading?: boolean;
}> = ({ label, value, icon, hint, loading }) => (
  <Card padding="md">
    <StatLabel>
      {icon && <StatIcon>{icon}</StatIcon>}
      {label}
    </StatLabel>
    {loading ? (
      <div style={{ marginTop: "var(--space-3)" }}>
        <Skeleton width="60%" height={28} />
      </div>
    ) : (
      <StatValue>
        {typeof value === "number" ? value.toLocaleString() : value}
      </StatValue>
    )}
    {hint && <StatHint>{hint}</StatHint>}
  </Card>
);

export const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--space-3);
  margin-top: var(--space-4);
`;

export const PlatformChip = styled.div`
  padding: var(--space-3);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
`;

export const PlatformValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
  margin-top: 2px;
`;

// ─── Funnel ───────────────────────────────────────────────────────────────────

const FunnelRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-2);
`;

const FunnelLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const FunnelSubLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
`;

const FunnelCount = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

const rateColor = (rate: number, isFirst: boolean) =>
  isFirst
    ? "var(--color-accent)"
    : rate > 50
    ? "var(--color-success)"
    : rate > 25
    ? "var(--color-warning)"
    : "var(--color-danger)";

const rateTextColor = (rate: number, isFirst: boolean) =>
  isFirst
    ? "var(--color-accent-text)"
    : rate > 50
    ? "var(--color-success)"
    : rate > 25
    ? "var(--color-warning)"
    : "var(--color-danger)";

const FunnelPct = styled.div<{ $rate: number; $isFirst: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  min-width: 64px;
  text-align: right;
  color: ${(p) => rateTextColor(p.$rate, p.$isFirst)};
`;

const FunnelTrack = styled.div`
  width: 100%;
  height: 32px;
  background: var(--color-surface-inset);
  border-radius: var(--radius-md);
  overflow: hidden;
  margin-bottom: var(--space-4);
`;

const FunnelFill = styled.div<{ $rate: number; $isFirst: boolean }>`
  height: 100%;
  width: ${(p) => Math.max(Math.min(p.$rate, 100), 1)}%;
  background: ${(p) => rateColor(p.$rate, p.$isFirst)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-inverse);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  transition: width var(--duration-slow) var(--ease-standard);
`;

export type FunnelStep = { label: string; count: number };

// Renders an ordered funnel. Percentages are vs. the first step; the sub-label
// shows step-to-step retention so the drop-offs are obvious.
export const Funnel: React.FC<{ steps: FunnelStep[] }> = ({ steps }) => {
  const first = steps.length > 0 ? Math.max(steps[0].count, 1) : 1;
  return (
    <>
      {steps.map((step, index) => {
        const isFirst = index === 0;
        const rate = (step.count / first) * 100;
        const prev = index > 0 ? steps[index - 1] : null;
        const stepPct = prev
          ? ((step.count / Math.max(prev.count, 1)) * 100).toFixed(1)
          : "100.0";
        return (
          <div key={`${step.label}-${index}`}>
            <FunnelRow>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FunnelLabel>{step.label}</FunnelLabel>
                {!isFirst && (
                  <FunnelSubLabel>{stepPct}% from previous step</FunnelSubLabel>
                )}
              </div>
              <FunnelCount>{step.count.toLocaleString()}</FunnelCount>
              <FunnelPct $rate={rate} $isFirst={isFirst}>
                {rate.toFixed(1)}%
              </FunnelPct>
            </FunnelRow>
            <FunnelTrack>
              <FunnelFill $rate={rate} $isFirst={isFirst}>
                {step.count > 0 ? step.count.toLocaleString() : ""}
              </FunnelFill>
            </FunnelTrack>
          </div>
        );
      })}
    </>
  );
};

// Largest step-to-step drop-off in an ordered funnel. Returns null when there
// are fewer than two non-trivial steps.
export const computeBottleneck = (
  steps: FunnelStep[]
): { from: string; to: string; retained: number } | null => {
  let worst: { from: string; to: string; retained: number } | null = null;
  for (let i = 1; i < steps.length; i++) {
    const prev = steps[i - 1];
    if (prev.count <= 0) continue;
    const retained = (steps[i].count / prev.count) * 100;
    if (!worst || retained < worst.retained) {
      worst = { from: prev.label, to: steps[i].label, retained };
    }
  }
  return worst;
};

export const Callout = styled.div<{ $tone?: "accent" | "warning" }>`
  margin-top: var(--space-4);
  padding: var(--space-4) var(--space-5);
  border-radius: var(--radius-lg);
  ${(p) =>
    p.$tone === "warning"
      ? css`
          background: var(--color-warning-subtle, var(--color-surface-subtle));
          border: 1px solid var(--color-warning, var(--color-border));
        `
      : css`
          background: var(--color-accent-subtle);
          border: 1px solid transparent;
        `}
`;

export const CalloutLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-accent-text);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const CalloutValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
  margin-top: var(--space-2);
`;

export const CalloutHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-top: var(--space-2);
`;
